/**
 * i18n 多語系配置
 * 翻譯資料一律從後端 API 動態載入（sys_languages.lang_data 為 i18n SSOT）。
 *
 * ⚠️ 不再 bundle 靜態 resources：先前 `partialBundledLanguages: true` + bundled
 * `translation` namespace 會讓 i18next 認定該 namespace 已載入 → http-backend 永遠
 * 不 fetch 後端完整版 → Phase 16 清理後改走 DB 的純動態 namespace（myCalendar /
 * interiorDesign 等）拿不到翻譯、顯示原始 key。改由 backend 全權接管（Fix A）。
 * 啟動瞬間 t() 短暫 return 原始 key（<200ms，backend fetch 完即正確、自動 re-render）。
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// API 基底 URL（與 axios.ts 相同邏輯）
const API_BASE_URL = process.env.REACT_APP_API_URL === undefined
  ? 'http://localhost:10181'   // 開發環境（沒設定 .env）
  : process.env.REACT_APP_API_URL; // 生產：'' → 相對路徑；自訂：絕對 URL

i18n
  // 使用 HTTP 後端動態載入語系檔
  .use(HttpBackend)
  // 偵測使用者語言
  .use(LanguageDetector)
  // 將 i18n 實例傳遞給 react-i18next
  .use(initReactI18next)
  // 初始化 i18next
  .init({
    fallbackLng: 'en',
    lng: localStorage.getItem('i18nextLng') || 'zh-TW',
    load: 'currentOnly', // 只載入當前語系代碼（不自動回退抓主語系 zh、en）
    debug: false,

    // 語言偵測選項
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React 已經處理 XSS
    },

    // 命名空間
    ns: ['translation'],
    defaultNS: 'translation',

    // 不限制 supportedLngs，讓動態語系也能載入
    // supportedLngs 會在 SystemContext 載入後由 LanguageSwitcher 控制可選語系

    // HTTP 後端設定
    backend: {
      // 從後端 API 載入語系翻譯資料（使用完整 URL，dev/prod 皆正確）
      loadPath: `${API_BASE_URL}/api/sys_languages/{{lng}}`,

      // 自訂解析：API 回傳的是 SysLanguageResponse，翻譯資料在 lang_data 欄位
      parse: (data: string) => {
        try {
          const parsed = JSON.parse(data);
          // API 回傳格式: { id, lang_code, lang_cname, lang_ename, lang_data: {...}, is_active, ... }
          if (parsed.lang_data && typeof parsed.lang_data === 'object') {
            return parsed.lang_data;
          }
          // 如果直接是翻譯資料格式
          return parsed;
        } catch {
          return {};
        }
      },

      // 請求設定：帶入 auth token（因為 /api/sys_languages 需要認證）
      requestOptions: {
        cache: 'no-store',
      },
      customHeaders: () => {
        const token = localStorage.getItem('access_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    },

    // 翻譯尚未載入完成前不阻塞 render；react-i18next 會在 backend fetch 完成時 re-render
    react: {
      useSuspense: false,
    },
  });

export default i18n;
